import React, { useCallback, useEffect, useRef, useState } from 'react';

import {
  BarFill,
  BarTimer,
  CommentContainer,
  Container,
  Content,
  ContentContainer,
  RateContainer,
  StarContainer,
} from './styles';
import {
  CommentIcon,
  LogoRobotIcon,
  StarIcon,
} from '../../../assets/svg/SVGComponents';
import { useFeedback } from '../../../hooks/feedback';
import { IUpdateFeedbackProps } from '../../../dtos/IFeedbacksProps';
import { ISmallPopoverComponentProps } from '../../../dtos/IPopoverComponentsProps';

const TIMER_DURATION = 1000;
const INTERVAL_TIME = 50;
const FEEDBACK_SOURCE = 'small-popover';

const SmallPopover: React.FC<ISmallPopoverComponentProps> = ({
  positiontop,
  positionleft,
  closePopover,
  openLargePopover,
  style,
  openPopover,
}) => {
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [progress, setProgress] = useState(0);
  const { openFeedback, setOpenFeedback, feedbacks, updateFeedback } =
    useFeedback();

  const ratingRef = useRef(rating);
  ratingRef.current = rating;

  // Sincroniza rating com o feedback existente ao abrir
  useEffect(() => {
    if (feedbacks?.rating) {
      setRating(feedbacks.rating);
    }
  }, [feedbacks?.rating]);

  const saveRating = useCallback(() => {
    if (ratingRef.current !== (feedbacks?.rating ?? 0)) {
      const updateFeedbackData: IUpdateFeedbackProps = {
        rating: ratingRef.current,
        feedback_source: FEEDBACK_SOURCE,
      };
      updateFeedback(updateFeedbackData);
    }
  }, [feedbacks?.rating, updateFeedback]);

  // Contador regressivo — só depende de openPopover
  useEffect(() => {
    if (!openPopover) return undefined;

    setProgress(0);

    const timer = setTimeout(() => {
      saveRating();
      closePopover();
      setProgress(0);
    }, TIMER_DURATION);

    const interval = setInterval(() => {
      setProgress(prev => {
        const next = prev + (INTERVAL_TIME / TIMER_DURATION) * 100;
        return next >= 100 ? 100 : next;
      });
    }, INTERVAL_TIME);

    return () => {
      clearTimeout(timer);
      clearInterval(interval);
    };
  }, [openPopover]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleStarClick = useCallback((starIndex: number) => {
    setRating(starIndex);
  }, []);

  const handleButtonComment = useCallback(() => {
    if (openFeedback) {
      saveRating();
      setOpenFeedback(false);
      closePopover();
      openLargePopover();
    }
  }, [
    closePopover,
    openFeedback,
    openLargePopover,
    saveRating,
    setOpenFeedback,
  ]);

  const displayRating = hoverRating || rating;

  return (
    <Container
      style={style}
      positiontop={positiontop}
      positionleft={positionleft}
    >
      <BarTimer>
        <BarFill style={{ width: `${100 - progress}%` }} />
      </BarTimer>
      <ContentContainer>
        <Content>
          <LogoRobotIcon width="2rem" fill="#1e88e5" />

          <RateContainer>
            <h3>Avalie o conteúdo</h3>
            <StarContainer>
              {Array.from({ length: 5 }, (_, index) => (
                <StarIcon
                  key={index}
                  width="1.5rem"
                  onClick={() => handleStarClick(index + 1)}
                  fill={index < displayRating ? '#ff9800' : '#e2e2e2'}
                  onMouseEnter={() => setHoverRating(index + 1)}
                  onMouseLeave={() => setHoverRating(0)}
                />
              ))}
            </StarContainer>
          </RateContainer>

          <CommentContainer type="button" onClick={handleButtonComment}>
            <CommentIcon width="1.8rem" stroke="#1e88e5" strokeWidth={2} />
            <p>
              Deixe seu <br />
              comentário
            </p>
          </CommentContainer>
        </Content>
      </ContentContainer>
    </Container>
  );
};

export default SmallPopover;
